<template>
  <div class="nveasy-bluetooth">
    <!--  || uploadProgress.isPending -->
    <div v-if="isMobile()" class="device-info">
      <BtDeviceInfo
        :connected="isConnected"
        :device-info="bindDeviceInfo"
        :has-background="false"
      ></BtDeviceInfo>
      <div v-if="isConnected" class="nveasy-bluetooth__toolbar">
        <!-- :upload-progress="uploadProgress" -->
        <NveasyBluetoothProgress :download-progress="downloadProgress" />
      </div>
    </div>

    <article v-for="file in uploadProgressCompt" :key="file.id" class="nveasy-bluetooth__item">
      <header class="nveasy-bluetooth__item-header">
        <div class="nveasy-bluetooth__item-header-main">
          <h3 class="nveasy-bluetooth__item-title">{{ file.name }}</h3>
        </div>
        <div class="nveasy-bluetooth__item-actions"></div>
      </header>
      <div class="upload-status">
        <!-- <div v-if="file.success === true" class="upload-status__success">上传成功</div> -->
        <div v-if="file.success === false" class="upload-status__error">
          <span class="error-text">
            <i class="icon-error-warning-line t-iconfont"></i>
            上传失败
          </span>
          <spa class="error-button">
            <!-- 重新上传 -->
          </spa>
        </div>
        <div v-else class="upload-status__loading">
          <i class="icon-loader-2-fill t-iconfont"></i>
          上传中...
        </div>
      </div>
    </article>

    <template v-if="sortedFiles.length">
      <article v-for="file in sortedFiles" :key="file.id" class="nveasy-bluetooth__item">
        <header class="nveasy-bluetooth__item-header">
          <div class="nveasy-bluetooth__item-header-main">
            <h3 class="nveasy-bluetooth__item-title">{{ file.name }}</h3>
          </div>
          <div class="nveasy-bluetooth__item-actions">
            <button
              class="nveasy-bluetooth__item-action nveasy-bluetooth__item-action--primary"
              type="button"
              @click="sendFiles(file)"
            >
              发送
            </button>
            <button
              class="nveasy-bluetooth__item-action"
              :disabled="confirmingVoid || deletingFileId === file.id"
              type="button"
              @click="deleteOssFile(file)"
            >
              作废
            </button>
          </div>
        </header>

        <div class="nveasy-bluetooth__footer">
          <AudioPlayer
            :ref="(instance) => setPlayerRef(instance, file.id)"
            :audio-url="file.leftFileUrl"
            :is-mobile="true"
            :is-pc-detail="false"
            :is-quick="false"
            :show-download="false"
            @play="handlePlayerPlay(file.id)"
          />
        </div>
      </article>
    </template>
    <template v-else-if="!isLoading && !uploadProgressCompt.length">
      <div class="nveasy-bluetooth__empty">
        <img alt="" class="nveasy-bluetooth__empty-image" :src="emptyImage" />
        <span>暂无上传完成的录音</span>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, defineEmits } from 'vue'
import AudioPlayer from '@/components/audio-player.vue'
import NveasyBluetoothProgress from './nveasy-bluetooth-progress.vue'
import { useNveasyBluetoothStore } from '@/store/useNveasyBluetoothStore'
import BtDeviceInfo from '@/pages/app/my-bluetooth/components/components/bt-device-info.vue'
import emptyImage from '@/assets/ai-logo-empty.png'
import xbb from '@xbb/xbb-utils'
import { isMobile } from '@/utils'
import { showConfirmDialog } from 'vant'

const emit = defineEmits(['send'])
// 文件模块
import { voidBluetoothBadgeRecord } from '@/api/nveasy-bt.js'

const nveasyBluetoothStore = useNveasyBluetoothStore()
import { storeToRefs } from 'pinia'

const { downloadProgress, uploadProgress, ossFiles, isConnected, bindDeviceInfo } =
  storeToRefs(nveasyBluetoothStore)
const isLoading = ref(false)

const uploadProgressCompt = computed(() => {
  return uploadProgress.value.formatFileList
    .filter((item) => item.success !== true)
    .map((item) => {
      return {
        ...item,
        name: xbb.formatTime(new Date(item.startTime), 'yyyy-MM-dd HH:mm') + ' 录音'
      }
    })
})

// 按文件 id 缓存播放器实例，currentPlayer 指向当前正在播放的那个
const playerRefMap = {}
let currentPlayer = null

const setPlayerRef = (instance, fileId) => {
  if (instance) {
    playerRefMap[fileId] = instance
  } else {
    delete playerRefMap[fileId]
  }
}

// 播放新的音频前，先调用上一个实例的 handlePause 停掉它，再把当前实例记为正在播放的
const handlePlayerPlay = (fileId) => {
  const nextPlayer = playerRefMap[fileId]
  if (!nextPlayer || nextPlayer === currentPlayer) return
  if (currentPlayer) {
    currentPlayer.handlePause()
  }
  currentPlayer = nextPlayer
}

const fetchFileList = async () => {
  isLoading.value = true
  try {
    await nveasyBluetoothStore.getOssFiles()
    if (isMobile()) {
      await nveasyBluetoothStore.queryFiles()
      nveasyBluetoothStore.uploadLocalFiles()
    }
  } finally {
    isLoading.value = false
  }
}

const sortedFiles = computed(() => {
  return ossFiles.value.map((item) => {
    return {
      ...item,
      name: xbb.formatTime(new Date(item.startTime), 'yyyy-MM-dd HH:mm') + ' 录音'
    }
  })
})

const lastSendAt = new Map()
const confirmingVoid = ref(false)
const deletingFileId = ref(null)

const sendFiles = (file) => {
  const now = Date.now()
  if (now - (lastSendAt.get(file.id) || 0) < 800) return
  lastSendAt.set(file.id, now)
  emit('send', file)
}

const deleteOssFile = async (file) => {
  if (confirmingVoid.value || deletingFileId.value !== null) return
  confirmingVoid.value = true
  try {
    await showConfirmDialog({
      title: '确认作废？',
      message: '作废后录音不可恢复',
      cancelButtonText: '取消',
      confirmButtonText: '确定'
    })
  } catch (error) {
    return
  } finally {
    confirmingVoid.value = false
  }

  deletingFileId.value = file.id
  try {
    await voidBluetoothBadgeRecord({ id: file.id })
    await nveasyBluetoothStore.getOssFiles()
  } finally {
    deletingFileId.value = null
  }
}

onMounted(() => {
  fetchFileList()
})

onBeforeUnmount(() => {
  if (currentPlayer) {
    currentPlayer.handlePause()
  }
  currentPlayer = null
})
</script>

<style lang="scss" scoped>
.nveasy-bluetooth {
  display: flex;
  flex-direction: column;
  background: #fff;

  .device-info {
    box-sizing: border-box;
    border: 1px solid #e5e6eb;
    border-radius: 16px;
    margin-bottom: 12px;
  }

  &__toolbar {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 12px;
    padding: 0 12px;
  }

  &__item {
    box-sizing: border-box;
    padding: 12px;
    border: 1px solid #e5e6eb;
    border-radius: 12px;

    &:not(&:last-child) {
      margin-bottom: 8px;
    }

    .upload-status {
      font-size: 12px;
      line-height: 16px;
      &__success {
      }
      &__error {
        display: flex;
        justify-content: space-between;
        .error-text {
          .t-iconfont {
            font-size: 12px;
            margin-right: 4px;
          }
          color: #f54e4e;
        }
        .error-button {
          color: #248df5;
          cursor: pointer;
        }
      }
      &__loading {
        .t-iconfont {
          font-size: 12px;
          margin-right: 4px;
        }
        color: #969799;
      }
    }
  }

  &__item-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  &__item-header-main {
    flex: 1;
    min-width: 0;
    padding-right: 12px;
  }

  &__item-title {
    margin: 0;
    color: #323233;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
  }

  &__item-subtitle {
    margin: 4px 0 0;
    color: #646566;
    font-size: 12px;
    line-height: 18px;
    word-break: break-all;
  }

  &__item-actions {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }

  &__item-action {
    padding: 0;
    border: 0;
    background: transparent;
    color: #969799;
    font-size: 14px;
    line-height: 20px;
    cursor: pointer;

    &--primary {
      margin-right: 12px;
      color: #722ed1;
    }
  }

  &__footer {
    :deep(.audio-player--mobile) {
      background: #fff;

      .audio-player__content {
        background: transparent !important;

        .audio-player__play {
          margin: 0;
          .t-iconfont {
            font-size: 20px !important;
          }
        }

        .audio-player__bar {
          display: flex !important;
          flex-direction: row !important;
          align-items: center;
        }
      }
    }
  }

  &__load-more {
    display: flex;
    justify-content: center;
    margin-top: 12px;
  }

  &__load-more-button {
    padding: 0;
    border: 0;
    background: transparent;
    color: #722ed1;
    font-size: 14px;
    line-height: 20px;
    cursor: pointer;

    &:disabled {
      color: #969799;
      cursor: not-allowed;
    }
  }

  &__item-meta {
    display: flex;
    flex-wrap: wrap;
    margin: -2px -6px;
  }

  &__item-meta-item {
    margin: 2px 6px;
    color: #969799;
    font-size: 12px;
    line-height: 18px;
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 280px;
    color: #969799;
    font-size: 14px;
    line-height: 20px;
    text-align: center;
  }

  &__empty-image {
    width: 169px;
    height: 112px;
    object-fit: contain;
    margin-bottom: 16px;
  }
}
</style>
